import { useMemo } from "react";
import type { Options } from "react-select";

import type { GroupedOptionsType } from "../../common-ui/form-fields/Dropdown";
import { useAccount } from "../../services/settings/hooks/useAccount";
import {
  addFavouritesToOptions,
  addRecentToOptions,
} from "../utils/DropdownUtils";
import type { DropdownOption } from "../utils/optionTransformers";
import type { Route } from "./fragments/gql/Route";
import type { QueryResult } from "./types/QueryResult";
import { useAllRoutesAsOptions } from "./useAllRoutesAsOptions";
import { useRecentRouteCodes } from "./useRecentBookings";

export const useAllRoutesAsOptionsWithFavouriteOptions = (options?: {
  skip?: boolean;
  includeClosedRoutes?: boolean;
}): QueryResult<
  GroupedOptionsType<DropdownOption<Route>> | Options<DropdownOption<Route>>
> => {
  const settings = useAccount();
  const recentRouteCodes = useRecentRouteCodes();
  const [routeOptions, loading, error] = useAllRoutesAsOptions(options);

  return useMemo(() => {
    let result:
      | GroupedOptionsType<DropdownOption<Route>>
      | Options<DropdownOption<Route>> = routeOptions;

    if (
      settings.accountSettings.favouriteRoutes &&
      settings.accountSettings.favouriteRoutes.length > 0
    ) {
      result = addFavouritesToOptions(
        result,
        settings.accountSettings.favouriteRoutes
          .filter((r) => options?.includeClosedRoutes || !r.isClosed)
          .map((route) => route.code),
      );
    }

    if (recentRouteCodes.length > 0) {
      result = addRecentToOptions(result, recentRouteCodes);
    }

    return [result, loading, error];
  }, [
    error,
    loading,
    routeOptions,
    settings.accountSettings.favouriteRoutes,
    options?.includeClosedRoutes,
    recentRouteCodes,
  ]);
};
